import React, { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import Marquee from "react-fast-marquee";
import { Leaf, Sparkles, ShieldCheck, Globe2, ArrowUpRight } from "lucide-react";
import { api, GROUP_IMG, TEXTURES, track } from "../lib/api";
import { Section, Reveal, Overline, Button, KineticHeading, ease } from "../components/primitives";
import Seo from "../components/Seo";

const PILLARS = [
  { icon: Leaf, title: "Rooted in Nature", body: "Botanical ingredients inspired by nature and India's rich ingredient heritage." },
  { icon: Sparkles, title: "Thoughtfully Selected", body: "Carefully selected ingredients created for the modern wellness consumer." },
  { icon: ShieldCheck, title: "Quality Focused", body: "A commitment to consistent, dependable product quality across our range." },
  { icon: Globe2, title: "Built for the World", body: "An India-origin brand designed and developed for global markets." },
];

const PARTNERS = [
  { title: "Importers", body: "For companies seeking Indian botanical and superfood products." },
  { title: "Distributors", body: "For companies developing regional or national distribution." },
  { title: "Retailers", body: "For health, wellness, food and specialty retail." },
  { title: "E-commerce", body: "For online retailers and marketplace businesses." },
];

const MARKETS = ["Middle East", "Europe", "North America", "Asia-Pacific", "Africa", "United Kingdom"];

const WORLD_MAP = "https://static.prod-images.emergentagent.com/jobs/4fc941ff-ab77-44f7-879a-f53a420285d2/images/154bb633c706de0220efaaf1828a875d91dc1f6f63aa4ffba668d9a5ce4ce258.jpeg";
const ORIGIN = { x: 905, y: 335 };
const ARC = "#B8641B";
const TARGETS = [
  { x: 660, y: 205, label: "Europe" },
  { x: 300, y: 250, label: "North America" },
  { x: 800, y: 320, label: "Middle East" },
  { x: 690, y: 470, label: "Africa" },
  { x: 1080, y: 560, label: "Asia-Pacific" },
];

export default function Home() {
  const [products, setProducts] = useState([]);
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.08]);

  useEffect(() => {
    api.get("/products").then((r) => setProducts(r.data)).catch(() => {});
    track("product_views", { page: "home" });
  }, []);

  return (
    <div>
      <Seo title="Pure Plants. Better Living." description="Cultiva Botanics — a premium India-origin botanical wellness and plant-based superfood brand, building international partnerships with importers, distributors, wholesalers and retailers." />

      {/* HERO */}
      <section ref={heroRef} className="relative overflow-hidden grain">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-20 pt-16 pb-20 lg:pt-24 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-6 relative z-10">
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1 }}>
              <Overline>India-Origin • Premium Botanicals • Global Partnerships</Overline>
            </motion.div>
            <KineticHeading
              lines={["Pure Plants.", "Better Living."]}
              className="text-6xl sm:text-7xl lg:text-8xl text-botanical mt-6"
            />
            <motion.p
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.7, duration: 0.9, ease }}
              className="mt-8 max-w-md text-lg text-muted-foreground font-light leading-relaxed"
            >
              Botanical wellness and plant-based superfoods rooted in nature and made for modern living.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.9, duration: 0.9, ease }}
              className="mt-10 flex flex-wrap gap-4"
            >
              <Button to="/shop" testId="hero-explore-btn">Explore Products</Button>
              <Button to="/partner" variant="outline" testId="hero-partner-btn">Partner With Us</Button>
            </motion.div>
          </div>
          <div className="lg:col-span-6 relative">
            <motion.div style={{ y: imgY, scale: imgScale }} className="relative">
              <motion.img
                src={GROUP_IMG} alt="Cultiva Botanics botanical superfood product range"
                initial={{ opacity: 0, scale: 1.05 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 1.4, ease }}
                className="w-full object-contain drop-shadow-2xl"
              />
            </motion.div>
            <div className="absolute -z-10 top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[120%] aspect-square rounded-full bg-moringa/5 blur-3xl" />
          </div>
        </div>
      </section>

      {/* MARQUEE */}
      <div className="border-y border-border py-6 bg-muted/40">
        <Marquee speed={40} gradient={false}>
          {["Moringa", "Hibiscus", "Beetroot", "Amla", "Turmeric", "Makhana", "Botanical Wellness", "Plant-Based Superfoods"].map((t, i) => (
            <span key={i} className="font-serif text-4xl md:text-5xl text-botanical/80 mx-12 italic">{t}<span className="text-turmeric mx-12 not-italic">✦</span></span>
          ))}
        </Marquee>
      </div>

      {/* PRODUCT SHOWCASE */}
      <Section className="py-28">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-16">
          <div>
            <Overline>01 — The Range</Overline>
            <h2 className="display text-5xl md:text-6xl text-botanical mt-4">Discover Our<br />Botanicals</h2>
          </div>
          <p className="max-w-sm text-muted-foreground font-light">Thoughtfully selected ingredients for everyday wellness and modern living.</p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {products.map((p, i) => (
            <Reveal key={p.slug} delay={(i % 3) * 0.08}>
              <Link
                to={`/products/${p.slug}`} onClick={() => track("product_cta_clicks", { slug: p.slug })}
                className="group block relative bg-white border border-border rounded-md overflow-hidden hover:shadow-xl hover:shadow-botanical/5 transition-all duration-500"
                data-testid={`product-card-${p.slug}`}
              >
                <div className="relative aspect-[4/5] overflow-hidden" style={{ background: `${p.color}0d` }}>
                  <img src={p.image} alt={p.name} loading="lazy" className="w-full h-full object-contain p-6 group-hover:scale-105 transition-transform duration-700" />
                  <span className="absolute top-4 left-4 h-2.5 w-2.5 rounded-full" style={{ background: p.color }} />
                </div>
                <div className="p-7 border-t border-border">
                  <div className="text-[11px] uppercase tracking-widest" style={{ color: p.color }}>{p.positioning}</div>
                  <h3 className="font-serif text-3xl text-botanical mt-2">{p.name}</h3>
                  <p className="text-sm text-muted-foreground mt-2 line-clamp-2">{p.short_description}</p>
                  <span className="mt-5 inline-flex items-center gap-1.5 text-sm font-medium text-botanical">
                    Explore Product <ArrowUpRight className="h-4 w-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                  </span>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </Section>

      {/* WHY CULTIVA */}
      <Section className="py-28 bg-botanical text-ivory relative grain rounded-none">
        <div className="relative z-10">
          <Reveal><Overline className="text-ivory/50">02 — Why Cultiva Botanics</Overline></Reveal>
          <Reveal delay={0.1}><h2 className="display text-5xl md:text-6xl mt-4 max-w-2xl">A brand built on nature and intent.</h2></Reveal>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px mt-16 bg-ivory/10">
            {PILLARS.map((p, i) => (
              <Reveal key={p.title} delay={i * 0.08} className="bg-botanical p-8">
                <p.icon className="h-8 w-8 text-turmeric" strokeWidth={1.2} />
                <h3 className="font-serif text-2xl mt-6">{p.title}</h3>
                <p className="text-ivory/65 text-sm mt-3 leading-relaxed">{p.body}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </Section>

      {/* FROM NATURE TO NOURISHMENT (editorial) */}
      <Section className="py-28">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          <Reveal>
            <div className="relative overflow-hidden rounded-md">
              <img src={TEXTURES.leaf} alt="Botanical leaves texture" loading="lazy" className="w-full h-[560px] object-cover" />
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <Overline>03 — From Nature to Nourishment</Overline>
            <h2 className="display text-5xl md:text-6xl text-botanical mt-4">Nature, made simple<br />for modern living.</h2>
            <p className="text-muted-foreground font-light text-lg mt-8 leading-relaxed">
              We believe natural botanical ingredients should be easier to discover, understand and bring into everyday life. Cultiva Botanics combines India's botanical heritage with modern presentation and a quality-focused approach.
            </p>
            <div className="mt-8"><Button to="/our-story" variant="ghost">Read Our Story</Button></div>
          </Reveal>
        </div>
      </Section>

      {/* FROM INDIA TO THE WORLD */}
      <Section className="py-28 rounded-none">
        <div className="text-center max-w-3xl mx-auto">
          <Reveal><Overline>04 — From India to the World</Overline></Reveal>
          <Reveal delay={0.1}><h2 className="display text-5xl md:text-6xl text-botanical mt-4">Rooted in India.<br />Built for global markets.</h2></Reveal>
          <Reveal delay={0.2}><p className="text-muted-foreground font-light text-lg mt-6">Rooted in India's rich botanical and superfood heritage, Cultiva Botanics is building a modern portfolio for consumers and business partners across global markets.</p></Reveal>
        </div>
        <Reveal delay={0.2}>
          <div className="relative mt-16 max-w-5xl mx-auto" data-testid="world-map">
            <div className="relative w-full" style={{ aspectRatio: "1264 / 848" }}>
              <img src={WORLD_MAP} alt="Cultiva Botanics global focus markets — world map with India as origin" loading="lazy" className="absolute inset-0 w-full h-full object-contain" />
              <svg viewBox="0 0 1264 848" preserveAspectRatio="xMidYMid meet" className="absolute inset-0 w-full h-full">
                <defs>
                  <radialGradient id="pulse" cx="50%" cy="50%" r="50%">
                    <stop offset="0%" stopColor={ARC} stopOpacity="0.4" />
                    <stop offset="100%" stopColor={ARC} stopOpacity="0" />
                  </radialGradient>
                </defs>
                {TARGETS.map((t, i) => (
                  <g key={i}>
                    <path d={`M${ORIGIN.x} ${ORIGIN.y} Q ${(ORIGIN.x + t.x) / 2} ${Math.min(t.y, ORIGIN.y) - 100} ${t.x} ${t.y}`} fill="none" stroke={ARC} strokeWidth="2.5" strokeLinecap="round" strokeDasharray="1 9" opacity="0.9">
                      <animate attributeName="stroke-dashoffset" from="20" to="0" dur="1.5s" repeatCount="indefinite" />
                    </path>
                    <circle cx={t.x} cy={t.y} r="8" fill="#F9F6F0" />
                    <circle cx={t.x} cy={t.y} r="5" fill={ARC} />
                    <text x={t.x} y={t.y - 16} textAnchor="middle" fontSize="17" fill="#1A3622" fontWeight="700" className="font-sans" style={{ paintOrder: "stroke", stroke: "#F9F6F0", strokeWidth: 4 }}>{t.label}</text>
                  </g>
                ))}
                <circle cx={ORIGIN.x} cy={ORIGIN.y} r="42" fill="url(#pulse)" />
                <circle cx={ORIGIN.x} cy={ORIGIN.y} r="11" fill="#F9F6F0" />
                <circle cx={ORIGIN.x} cy={ORIGIN.y} r="7" fill={ARC} />
                <text x={ORIGIN.x} y={ORIGIN.y + 42} textAnchor="middle" fontSize="19" fill="#1A3622" fontWeight="800" className="font-sans" style={{ paintOrder: "stroke", stroke: "#F9F6F0", strokeWidth: 5 }}>India — Origin</text>
              </svg>
            </div>
            <div className="flex flex-wrap justify-center gap-3 mt-6">
              <span className="overline text-earth">Global Focus Markets:</span>
              {MARKETS.map((m) => <span key={m} className="text-sm text-botanical/80 border border-border rounded-full px-4 py-1.5">{m}</span>)}
            </div>
          </div>
        </Reveal>
      </Section>

      {/* B2B CTA */}
      <Section className="py-28">
        <Reveal>
          <div className="relative overflow-hidden rounded-md bg-forest text-ivory grain">
            <img src={TEXTURES.shipping} alt="" loading="lazy" className="absolute inset-0 w-full h-full object-cover opacity-20" />
            <div className="relative z-10 px-8 md:px-16 py-20 max-w-3xl">
              <Overline className="text-ivory/50">Global Business</Overline>
              <h2 className="display text-5xl md:text-6xl mt-4">Bring Cultiva Botanics to Your Market</h2>
              <p className="text-ivory/70 font-light text-lg mt-6">We are developing international partnerships with importers, distributors, wholesalers, retailers and e-commerce businesses looking to bring botanical wellness and superfood products to their customers.</p>
              <div className="mt-10 flex flex-wrap gap-4">
                <Button to="/become-a-distributor" variant="light" testId="home-distributor-btn">Become a Distributor</Button>
                <Button to="/wholesale" variant="outline" className="border-ivory/30 text-ivory hover:bg-ivory hover:text-botanical" testId="home-wholesale-btn">Wholesale & Export Enquiry</Button>
              </div>
            </div>
          </div>
        </Reveal>
      </Section>

      {/* GLOBAL PARTNERSHIPS */}
      <Section className="py-28 pt-0">
        <div className="mb-14">
          <Reveal><Overline>05 — Built for Global Partnerships</Overline></Reveal>
          <Reveal delay={0.1}><h2 className="display text-5xl md:text-6xl text-botanical mt-4 max-w-2xl">Partnership models for every market.</h2></Reveal>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {PARTNERS.map((p, i) => (
            <Reveal key={p.title} delay={i * 0.08}>
              <div className="border border-border rounded-md p-8 h-full hover:border-botanical/40 transition-colors bg-white">
                <div className="font-serif text-5xl text-turmeric">0{i + 1}</div>
                <h3 className="font-serif text-3xl text-botanical mt-6">{p.title}</h3>
                <p className="text-sm text-muted-foreground mt-3 leading-relaxed">{p.body}</p>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.1}><div className="mt-12"><Button to="/partner">Partner With Cultiva Botanics</Button></div></Reveal>
      </Section>

      {/* FINAL CTA */}
      <Section className="py-28 bg-botanical text-ivory rounded-none text-center grain">
        <div className="relative z-10 max-w-3xl mx-auto">
          <Reveal><h2 className="display text-6xl md:text-7xl">Let's Grow Together</h2></Reveal>
          <Reveal delay={0.1}><p className="text-ivory/70 font-light text-lg mt-6">Are you an importer, distributor, retailer or wholesale buyer looking for botanical wellness and superfood products? Tell us about your market and let's explore the opportunity.</p></Reveal>
          <Reveal delay={0.2}><div className="mt-10 flex justify-center"><Button to="/partner" variant="light" testId="final-cta-btn">Start a Business Conversation</Button></div></Reveal>
        </div>
      </Section>
    </div>
  );
}
